import { EditIcon, UnlockIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import Card from 'components/card/Card';
import ErrorState from 'components/states/ErrorState';
import PageCard from 'components/page-card';
import SkeletonTable from 'components/states/SkeletonTable';
import {
  tableCellProps,
  tableHeaderTextProps,
  tableProps,
  tableRowProps,
  tableThFlexProps,
  tableThProps,
} from 'theme/constants';
import { translate } from '../../../i18n';
import { AccessControlConfigDto } from '../../../models/access-control/dto/access-control-config.dto';
import { useGetAccessControlConfigs } from '../hooks/useGetAccessControlConfigs';
import { useGetLocations } from '../../location-management/hooks/useGetLocations';
import { useElevatedAdminAccess } from '../../organization/hooks/useIsElevatedAdmin';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { getProviderLabel } from '../utils/access-control.utils';
import AccessControlConfigModal from './AccessControlConfigModal';
import AccessControlCredentialsModal from './AccessControlCredentialsModal';

const columnHelper = createColumnHelper<AccessControlConfigDto>();

export default function AccessControlSettingsCard() {
  const { selectedOrganization } = useSelectedOrganization();
  const { isElevated: isElevatedAdmin } = useElevatedAdminAccess();
  const { data: configs, isLoading, isError } = useGetAccessControlConfigs(
    selectedOrganization?.id,
  );
  const { data: locations } = useGetLocations(selectedOrganization?.id || '');
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingConfig, setEditingConfig] = useState<AccessControlConfigDto | null>(null);
  const {
    isOpen: isCredentialsOpen,
    onOpen: onCredentialsOpen,
    onClose: onCredentialsClose,
  } = useDisclosure();
  const [credentialsConfig, setCredentialsConfig] =
    useState<AccessControlConfigDto | null>(null);

  const handleAdd = () => {
    setEditingConfig(null);
    onOpen();
  };

  const handleEdit = (config: AccessControlConfigDto) => {
    setEditingConfig(config);
    onOpen();
  };

  const handleSetCredentials = (config: AccessControlConfigDto) => {
    setCredentialsConfig(config);
    onCredentialsOpen();
  };

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (locations || []).forEach((location) => map.set(location.id, location.name));
    return map;
  }, [locations]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('provider', {
        id: 'provider',
        header: () => <Text {...tableHeaderTextProps}>{translate('access_control.provider' as any)}</Text>,
        cell: (info) => (
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {getProviderLabel(info.getValue())}
          </Text>
        ),
      }),
      columnHelper.accessor('locationId', {
        id: 'location',
        header: () => <Text {...tableHeaderTextProps}>{translate('access_control.location' as any)}</Text>,
        cell: (info) => {
          const locationId = info.getValue();
          return (
            <Text color={textColor} fontSize="sm">
              {locationId
                ? locationNameById.get(locationId) || locationId
                : translate('access_control.org_wide' as any)}
            </Text>
          );
        },
      }),
      columnHelper.accessor('doorMappings', {
        id: 'doorMappings',
        header: () => <Text {...tableHeaderTextProps}>{translate('access_control.door_mappings' as any)}</Text>,
        cell: (info) => (
          <Text color={textColor} fontSize="sm">
            {Object.keys(info.getValue() || {}).length}
          </Text>
        ),
      }),
      columnHelper.accessor('hasCredentials', {
        id: 'hasCredentials',
        header: () => (
          <Text {...tableHeaderTextProps}>
            {translate('access_control.credentials' as any)}
          </Text>
        ),
        cell: (info) => (
          <Badge colorScheme={info.getValue() ? 'green' : 'orange'}>
            {info.getValue()
              ? translate('access_control.credentials_set' as any)
              : translate('access_control.credentials_missing' as any)}
          </Badge>
        ),
      }),
      columnHelper.accessor('enabled', {
        id: 'enabled',
        header: () => <Text {...tableHeaderTextProps}>{translate('access_control.enabled' as any)}</Text>,
        cell: (info) => (
          <Badge colorScheme={info.getValue() ? 'green' : 'gray'}>
            {info.getValue()
              ? translate('access_control.enabled' as any)
              : translate('access_control.disabled' as any)}
          </Badge>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <Text {...tableHeaderTextProps}>{translate('access_control.actions' as any)}</Text>,
        cell: (info) => (
          <Flex gap={1}>
            <IconButton
              aria-label={translate('access_control.edit_config' as any)}
              icon={<EditIcon />}
              size="sm"
              variant="ghost"
              colorScheme="brand"
              onClick={() => handleEdit(info.row.original)}
            />
            <IconButton
              aria-label={translate('access_control.set_credentials' as any)}
              icon={<UnlockIcon />}
              size="sm"
              variant="ghost"
              colorScheme="orange"
              onClick={() => handleSetCredentials(info.row.original)}
            />
          </Flex>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [textColor, locationNameById],
  );

  const table = useReactTable({
    data: configs || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!selectedOrganization) {
    return null;
  }

  if (!isElevatedAdmin) {
    return null;
  }

  if (isLoading) {
    return (
      <PageCard title={translate('access_control.title' as any)}>
        <SkeletonTable rows={3} columns={5} />
      </PageCard>
    );
  }

  if (isError) {
    return (
      <Card flexDirection="column" w="100%" px="0px">
        <ErrorState message={translate('access_control.load_error' as any)} />
      </Card>
    );
  }

  return (
    <>
      <PageCard
        title={translate('access_control.title' as any)}
        rightHeader={
          <Button colorScheme="brand" size="sm" onClick={handleAdd}>
            {translate('access_control.add_config' as any)}
          </Button>
        }
      >
        <Box>
          {configs && configs.length > 0 ? (
            <Table {...tableProps}>
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th key={header.id} colSpan={header.colSpan} {...tableThProps}>
                        <Flex {...tableThFlexProps}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map((row) => (
                  <Tr key={row.id} {...tableRowProps} cursor="default" _hover={{}}>
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id} {...tableCellProps}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Box textAlign="center" py={8}>
              <Text color="gray.500" fontSize="lg">
                {translate('access_control.no_configs_found' as any)}
              </Text>
            </Box>
          )}
        </Box>
      </PageCard>

      <AccessControlConfigModal
        isOpen={isOpen}
        onClose={onClose}
        organizationId={selectedOrganization.id}
        editingConfig={editingConfig}
        locations={locations || []}
      />

      <AccessControlCredentialsModal
        isOpen={isCredentialsOpen}
        onClose={onCredentialsClose}
        organizationId={selectedOrganization.id}
        config={credentialsConfig}
      />
    </>
  );
}
